import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import Navigation from '../components/layout/Navigation';
import { Package, Eye, Download, ExternalLink, ChevronDown, Plus, Tag, Trash2, ToggleLeft, ToggleRight } from 'lucide-react';

const Admin = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('orders'); // orders | discounts
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [updatingStatus, setUpdatingStatus] = useState(null);
  
  // Discount codes state
  const [discountCodes, setDiscountCodes] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creatingCode, setCreatingCode] = useState(false);
  const [newCode, setNewCode] = useState({
    code: '',
    discount_type: 'percentage',
    discount_value: '',
    min_order_amount: '',
    max_uses: '',
    valid_until: ''
  });

  useEffect(() => {
    if (activeTab === 'orders') {
      fetchOrders();
    } else if (activeTab === 'discounts') {
      fetchDiscountCodes();
    }
  }, [activeTab]);

  const fetchOrders = async () => {
    try {
      const response = await api.get('/admin/orders');
      setOrders(response.data.orders);
    } catch (error) {
      console.error('Failed to fetch orders:', error);
      if (error.response?.status === 403 || error.response?.status === 401) {
        alert('Access denied. Admin privileges required.');
        navigate('/');
      }
    } finally {
      setLoading(false);
    }
  };

  const updateOrderStatus = async (orderId, newStatus) => {
    setUpdatingStatus(orderId);
    try {
      await api.patch(`/admin/orders/${orderId}/status`, { status: newStatus });
      // Update local state
      setOrders(orders.map(order => 
        order.order_id === orderId 
          ? { ...order, status: newStatus }
          : order
      ));
      if (selectedOrder?.order_id === orderId) {
        setSelectedOrder({ ...selectedOrder, status: newStatus });
      }
    } catch (error) {
      console.error('Failed to update status:', error);
      alert('Failed to update order status');
    } finally {
      setUpdatingStatus(null);
    }
  };

  const viewOrderDetails = async (orderId) => {
    try {
      const response = await api.get(`/admin/orders/${orderId}`);
      setSelectedOrder(response.data);
    } catch (error) {
      console.error('Failed to fetch order details:', error);
      alert('Failed to load order details');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-900/30 text-yellow-400 border-yellow-600/50',
      processing: 'bg-blue-900/30 text-blue-400 border-blue-600/50',
      shipped: 'bg-purple-900/30 text-purple-400 border-purple-600/50',
      completed: 'bg-green-900/30 text-green-400 border-green-600/50',
      cancelled: 'bg-red-900/30 text-red-400 border-red-600/50'
    };
    return colors[status] || 'bg-gray-900/30 text-gray-400 border-gray-600/50';
  };

  // Discount Code Functions
  const fetchDiscountCodes = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/discount-codes');
      setDiscountCodes(response.data.discount_codes);
    } catch (error) {
      console.error('Failed to fetch discount codes:', error);
      if (error.response?.status === 403 || error.response?.status === 401) {
        alert('Access denied. Admin privileges required.');
        navigate('/');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCode = async (e) => {
    e.preventDefault();
    setCreatingCode(true);
    
    try {
      const payload = {
        code: newCode.code.toUpperCase().trim(),
        discount_type: newCode.discount_type,
        discount_value: parseFloat(newCode.discount_value),
        min_order_amount: newCode.min_order_amount ? parseFloat(newCode.min_order_amount) : null,
        max_uses: newCode.max_uses ? parseInt(newCode.max_uses) : null,
        valid_until: newCode.valid_until || null
      };

      await api.post('/admin/discount-codes', payload);
      
      // Reset form and close modal
      setNewCode({
        code: '',
        discount_type: 'percentage',
        discount_value: '',
        min_order_amount: '',
        max_uses: '',
        valid_until: ''
      });
      setShowCreateModal(false);
      
      // Refresh list
      fetchDiscountCodes();
    } catch (error) {
      console.error('Failed to create discount code:', error);
      alert(error.response?.data?.detail || 'Failed to create discount code');
    } finally {
      setCreatingCode(false);
    }
  };

  const toggleCodeActive = async (code) => {
    try {
      const response = await api.patch(`/admin/discount-codes/${code}/toggle`);
      // Update local state
      setDiscountCodes(discountCodes.map(dc => 
        dc.code === code ? { ...dc, active: response.data.active } : dc
      ));
    } catch (error) {
      console.error('Failed to toggle discount code:', error);
      alert('Failed to update discount code status');
    }
  };

  const deleteCode = async (code) => {
    if (!window.confirm(`¿Eliminar el código ${code}?`)) return;
    
    try {
      await api.delete(`/admin/discount-codes/${code}`);
      setDiscountCodes(discountCodes.filter(dc => dc.code !== code));
    } catch (error) {
      console.error('Failed to delete discount code:', error);
      alert('Failed to delete discount code');
    }
  };

  if (loading) {
    return (
      <>
        <Navigation />
        <div className="min-h-screen bg-brand-bg pt-24 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-violet-600"></div>
        </div>
      </>
    );
  }

  return (
    <>
      <Navigation />
      <div className="min-h-screen bg-brand-bg pt-24 px-6 pb-12">
        <div className="max-w-7xl mx-auto">
          <div className="mb-8">
            <h1 className="text-4xl font-outfit font-light tracking-tight text-white mb-2">
              Admin Panel
            </h1>
            <p className="text-zinc-400">Gestiona órdenes y códigos de descuento</p>
          </div>

          {/* Tabs */}
          <div className="flex space-x-2 mb-6">
            <button
              onClick={() => setActiveTab('orders')}
              className={`px-6 py-3 rounded-lg font-medium transition-all flex items-center space-x-2 ${
                activeTab === 'orders'
                  ? 'bg-violet-600 text-white'
                  : 'bg-zinc-900 text-zinc-400 hover:text-white'
              }`}
            >
              <Package size={20} />
              <span>Órdenes</span>
            </button>
            <button
              onClick={() => setActiveTab('discounts')}
              className={`px-6 py-3 rounded-lg font-medium transition-all flex items-center space-x-2 ${
                activeTab === 'discounts'
                  ? 'bg-violet-600 text-white'
                  : 'bg-zinc-900 text-zinc-400 hover:text-white'
              }`}
            >
              <Tag size={20} />
              <span>Códigos de Descuento</span>
            </button>
          </div>

          {/* Orders Tab */}
          {activeTab === 'orders' && (
            <div className="bg-brand-surface border border-brand-border rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-zinc-900 border-b border-brand-border">
                    <tr>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-zinc-400">Order ID</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-zinc-400">Customer</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-zinc-400">Total</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-zinc-400">Status</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-zinc-400">Date</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-zinc-400">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-brand-border">
                  {orders.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="px-6 py-12 text-center text-zinc-500">
                        No orders found
                      </td>
                    </tr>
                  ) : (
                    orders.map((order) => (
                      <tr key={order.order_id} className="hover:bg-zinc-900/50 transition-colors">
                        <td className="px-6 py-4">
                          <span className="font-mono text-sm text-white">
                            {order.order_id}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm">
                            <p className="text-white font-medium">
                              {order.shipping_address?.fullName || 'N/A'}
                            </p>
                            <p className="text-zinc-500 text-xs">
                              {order.guest_email || 'No email'}
                            </p>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-white font-bold">
                            {order.total_amount.toFixed(2)}€
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="relative inline-block">
                            <select
                              value={order.status}
                              onChange={(e) => updateOrderStatus(order.order_id, e.target.value)}
                              disabled={updatingStatus === order.order_id}
                              className={`appearance-none px-3 py-1 rounded-md border text-sm font-medium pr-8 cursor-pointer ${getStatusColor(order.status)} ${
                                updatingStatus === order.order_id ? 'opacity-50' : ''
                              }`}
                            >
                              <option value="pending">Pending</option>
                              <option value="processing">Processing</option>
                              <option value="shipped">Shipped</option>
                              <option value="completed">Completed</option>
                              <option value="cancelled">Cancelled</option>
                            </select>
                            <ChevronDown size={16} className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none text-current" />
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-zinc-400">
                          {new Date(order.created_at).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4">
                          <button
                            onClick={() => viewOrderDetails(order.order_id)}
                            className="flex items-center space-x-2 px-3 py-2 bg-violet-600 text-white rounded-md text-sm font-medium hover:bg-violet-500 transition-all"
                          >
                            <Eye size={16} />
                            <span>View</span>
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
          )}

          {/* Discount Codes Tab */}
          {activeTab === 'discounts' && (
            <div>
              {/* Create Button */}
              <div className="mb-6 flex justify-end">
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="flex items-center space-x-2 px-6 py-3 bg-violet-600 text-white rounded-lg font-medium hover:bg-violet-500 transition-all"
                >
                  <Plus size={20} />
                  <span>Crear Código</span>
                </button>
              </div>

              {/* Discount Codes Table */}
              <div className="bg-brand-surface border border-brand-border rounded-xl overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-zinc-900 border-b border-brand-border">
                      <tr>
                        <th className="px-6 py-4 text-left text-sm font-semibold text-zinc-400">Código</th>
                        <th className="px-6 py-4 text-left text-sm font-semibold text-zinc-400">Tipo & Valor</th>
                        <th className="px-6 py-4 text-left text-sm font-semibold text-zinc-400">Usos</th>
                        <th className="px-6 py-4 text-left text-sm font-semibold text-zinc-400">Mínimo</th>
                        <th className="px-6 py-4 text-left text-sm font-semibold text-zinc-400">Expira</th>
                        <th className="px-6 py-4 text-left text-sm font-semibold text-zinc-400">Estado</th>
                        <th className="px-6 py-4 text-left text-sm font-semibold text-zinc-400">Acciones</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-brand-border">
                      {discountCodes.length === 0 ? (
                        <tr>
                          <td colSpan="7" className="px-6 py-12 text-center text-zinc-500">
                            No hay códigos de descuento
                          </td>
                        </tr>
                      ) : (
                        discountCodes.map((code) => (
                          <tr key={code.code} className="hover:bg-zinc-900/50 transition-colors">
                            <td className="px-6 py-4">
                              <span className="font-mono text-lg font-bold text-violet-400">
                                {code.code}
                              </span>
                            </td>
                            <td className="px-6 py-4">
                              <div className="text-sm">
                                <p className="text-white font-medium">
                                  {code.discount_type === 'percentage' 
                                    ? `${code.discount_value}%`
                                    : `${code.discount_value}€`
                                  }
                                </p>
                                <p className="text-zinc-500 text-xs">
                                  {code.discount_type === 'percentage' ? 'Porcentaje' : 'Fijo'}
                                </p>
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <span className="text-white">
                                {code.current_uses || 0}
                                {code.max_uses ? ` / ${code.max_uses}` : ' / ∞'}
                              </span>
                            </td>
                            <td className="px-6 py-4 text-zinc-400 text-sm">
                              {code.min_order_amount ? `${code.min_order_amount}€` : '-'}
                            </td>
                            <td className="px-6 py-4 text-zinc-400 text-sm">
                              {code.valid_until 
                                ? new Date(code.valid_until).toLocaleDateString()
                                : 'Nunca'
                              }
                            </td>
                            <td className="px-6 py-4">
                              <button
                                onClick={() => toggleCodeActive(code.code)}
                                className={`flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-all ${
                                  code.active
                                    ? 'bg-green-900/30 text-green-400 border border-green-600/50'
                                    : 'bg-red-900/30 text-red-400 border border-red-600/50'
                                }`}
                              >
                                {code.active ? <ToggleRight size={16} /> : <ToggleLeft size={16} />}
                                <span>{code.active ? 'Activo' : 'Inactivo'}</span>
                              </button>
                            </td>
                            <td className="px-6 py-4">
                              <button
                                onClick={() => deleteCode(code.code)}
                                className="p-2 bg-red-900/20 text-red-400 border border-red-600/30 rounded-md hover:bg-red-900/40 transition-all"
                              >
                                <Trash2 size={16} />
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Create Discount Code Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-brand-surface border border-brand-border rounded-xl max-w-2xl w-full">
            <div className="p-6 border-b border-brand-border flex items-center justify-between">
              <h2 className="text-2xl font-outfit font-semibold text-white">
                Crear Código de Descuento
              </h2>
              <button
                onClick={() => setShowCreateModal(false)}
                className="text-zinc-400 hover:text-white transition-colors text-2xl"
              >
                ×
              </button>
            </div>

            <form onSubmit={handleCreateCode} className="p-6 space-y-6">
              {/* Code */}
              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-2">
                  Código *
                </label>
                <input
                  type="text"
                  required
                  value={newCode.code}
                  onChange={(e) => setNewCode({ ...newCode, code: e.target.value.toUpperCase() })}
                  placeholder="VERANO2024"
                  className="w-full bg-zinc-900 border border-zinc-800 text-white rounded-md px-4 py-3 focus:border-violet-500 focus:ring-1 focus:ring-violet-500 transition-all uppercase font-mono"
                />
              </div>

              {/* Discount Type & Value */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-zinc-400 mb-2">
                    Tipo *
                  </label>
                  <select
                    value={newCode.discount_type}
                    onChange={(e) => setNewCode({ ...newCode, discount_type: e.target.value })}
                    className="w-full bg-zinc-900 border border-zinc-800 text-white rounded-md px-4 py-3 focus:border-violet-500 focus:ring-1 focus:ring-violet-500 transition-all"
                  >
                    <option value="percentage">Porcentaje (%)</option>
                    <option value="fixed">Fijo (€)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-zinc-400 mb-2">
                    Valor *
                  </label>
                  <input
                    type="number"
                    required
                    step="0.01"
                    min="0"
                    max={newCode.discount_type === 'percentage' ? '100' : undefined}
                    value={newCode.discount_value}
                    onChange={(e) => setNewCode({ ...newCode, discount_value: e.target.value })}
                    placeholder={newCode.discount_type === 'percentage' ? '10' : '5.00'}
                    className="w-full bg-zinc-900 border border-zinc-800 text-white rounded-md px-4 py-3 focus:border-violet-500 focus:ring-1 focus:ring-violet-500 transition-all"
                  />
                </div>
              </div>

              {/* Optional Fields */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-zinc-400 mb-2">
                    Monto Mínimo (Opcional)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={newCode.min_order_amount}
                    onChange={(e) => setNewCode({ ...newCode, min_order_amount: e.target.value })}
                    placeholder="20.00"
                    className="w-full bg-zinc-900 border border-zinc-800 text-white rounded-md px-4 py-3 focus:border-violet-500 focus:ring-1 focus:ring-violet-500 transition-all"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-zinc-400 mb-2">
                    Usos Máximos (Opcional)
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={newCode.max_uses}
                    onChange={(e) => setNewCode({ ...newCode, max_uses: e.target.value })}
                    placeholder="100"
                    className="w-full bg-zinc-900 border border-zinc-800 text-white rounded-md px-4 py-3 focus:border-violet-500 focus:ring-1 focus:ring-violet-500 transition-all"
                  />
                </div>
              </div>

              {/* Valid Until */}
              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-2">
                  Válido Hasta (Opcional)
                </label>
                <input
                  type="date"
                  value={newCode.valid_until}
                  onChange={(e) => setNewCode({ ...newCode, valid_until: e.target.value })}
                  className="w-full bg-zinc-900 border border-zinc-800 text-white rounded-md px-4 py-3 focus:border-violet-500 focus:ring-1 focus:ring-violet-500 transition-all"
                />
              </div>

              {/* Submit Button */}
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="px-6 py-3 bg-zinc-800 text-zinc-300 rounded-lg font-medium hover:bg-zinc-700 transition-all"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={creatingCode}
                  className="px-6 py-3 bg-violet-600 text-white rounded-lg font-medium hover:bg-violet-500 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {creatingCode ? 'Creando...' : 'Crear Código'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Order Details Modal */}
      {selectedOrder && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-brand-surface border border-brand-border rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-brand-surface border-b border-brand-border p-6 flex items-center justify-between z-10">
              <div>
                <h2 className="text-2xl font-outfit font-semibold text-white">
                  Order Details
                </h2>
                <p className="text-zinc-400 text-sm mt-1">
                  {selectedOrder.order_id}
                </p>
              </div>
              <button
                onClick={() => setSelectedOrder(null)}
                className="text-zinc-400 hover:text-white transition-colors text-2xl"
              >
                ×
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Shipping Address */}
              <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-white mb-3">Shipping Address</h3>
                <div className="text-sm text-zinc-300 space-y-1">
                  <p>{selectedOrder.shipping_address?.fullName}</p>
                  <p>{selectedOrder.shipping_address?.address}</p>
                  <p>{selectedOrder.shipping_address?.city}, {selectedOrder.shipping_address?.postalCode}</p>
                  <p>{selectedOrder.shipping_address?.country}</p>
                  {selectedOrder.shipping_address?.phone && (
                    <p>Phone: {selectedOrder.shipping_address.phone}</p>
                  )}
                </div>
              </div>

              {/* Deck Link */}
              {selectedOrder.deck_link && (
                <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-4">
                  <h3 className="text-lg font-semibold text-white mb-3">Deck Link</h3>
                  <a
                    href={selectedOrder.deck_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center space-x-2 text-violet-400 hover:text-violet-300 text-sm"
                  >
                    <ExternalLink size={16} />
                    <span className="break-all">{selectedOrder.deck_link}</span>
                  </a>
                </div>
              )}

              {/* Order Items */}
              <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-white mb-3">Order Items</h3>
                <div className="space-y-4">
                  {selectedOrder.items.map((item, idx) => (
                    <div key={idx} className="border border-zinc-700 rounded-lg p-4">
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <h4 className="text-white font-medium">{item.product_name}</h4>
                          <p className="text-zinc-500 text-sm">Quantity: {item.quantity}</p>
                        </div>
                        <span className="text-white font-bold">{(item.price * item.quantity).toFixed(2)}€</span>
                      </div>

                      {/* Customization Details */}
                      {item.customization && (
                        <div className="mt-3 space-y-3">
                          {/* Card Back */}
                          {item.customization.card_back_url && (
                            <div>
                              <p className="text-zinc-400 text-sm mb-2">Card Back:</p>
                              <img
                                src={item.customization.card_back_url}
                                alt="Card Back"
                                className="w-24 h-32 object-cover rounded border border-zinc-700"
                              />
                            </div>
                          )}

                          {/* Cards List with Images */}
                          {item.customization.cards && item.customization.cards.length > 0 && (
                            <div>
                              <p className="text-zinc-400 text-sm mb-2">
                                Cards ({item.customization.cards.length}):
                              </p>
                              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 max-h-96 overflow-y-auto p-2 bg-zinc-950 rounded">
                                {item.customization.cards.map((card, cardIdx) => (
                                  <div key={cardIdx} className="group relative">
                                    {card.selected_version && (card.selected_version.image_uri || card.selected_version.image_url) ? (
                                      <div className="space-y-2">
                                        {/* Card Image */}
                                        <div className="relative rounded overflow-hidden border border-zinc-700 hover:border-violet-500 transition-all">
                                          <img
                                            src={card.selected_version.image_uri || card.selected_version.image_url}
                                            alt={card.name}
                                            className="w-full h-auto object-cover"
                                            loading="lazy"
                                          />
                                          {/* Overlay with download button */}
                                          <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                            <a
                                              href={card.selected_version.image_uri || card.selected_version.image_url}
                                              download={`${card.name.replace(/[^a-z0-9]/gi, '_')}.jpg`}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              className="px-3 py-2 bg-violet-600 text-white rounded text-xs font-medium hover:bg-violet-500"
                                            >
                                              <Download size={14} className="inline mr-1" />
                                              Descargar
                                            </a>
                                          </div>
                                        </div>
                                        {/* Card Info */}
                                        <div className="text-xs">
                                          <p className="text-white font-medium truncate">{card.name}</p>
                                          <p className="text-zinc-500 truncate">{card.selected_version.set_name}</p>
                                        </div>
                                      </div>
                                    ) : (
                                      // Fallback for cards without image
                                      <div className="bg-zinc-800 border border-zinc-700 rounded p-3 text-center">
                                        <p className="text-zinc-300 text-xs font-medium">{card.name}</p>
                                        {card.selected_version && (
                                          <p className="text-zinc-500 text-xs mt-1">{card.selected_version.set_name}</p>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Custom Images */}
                          {item.customization.custom_images && item.customization.custom_images.length > 0 && (
                            <div>
                              <p className="text-zinc-400 text-sm mb-2">
                                Custom Images ({item.customization.custom_images.length}):
                              </p>
                              <div className="flex flex-wrap gap-2">
                                {item.customization.custom_images.map((img, imgIdx) => (
                                  <a
                                    key={imgIdx}
                                    href={`/api/files/${img.file_id}`}
                                    download={img.original_filename}
                                    className="flex items-center space-x-2 px-3 py-2 bg-violet-600 text-white rounded text-xs hover:bg-violet-500 transition-all"
                                  >
                                    <Download size={14} />
                                    <span>{img.original_filename}</span>
                                  </a>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Comments */}
                          {item.customization.comments && (
                            <div>
                              <p className="text-zinc-400 text-sm mb-1">Comments:</p>
                              <p className="text-zinc-300 text-sm bg-zinc-950 rounded p-2">
                                {item.customization.comments}
                              </p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Total */}
              <div className="flex justify-between items-center pt-4 border-t border-zinc-800">
                <span className="text-xl font-semibold text-white">Total</span>
                <span className="text-2xl font-bold text-violet-600">
                  {selectedOrder.total_amount.toFixed(2)}€
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Admin;
